import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import FilterBar, { FilterSummary, defaultFilters, type FilterState } from '@/components/feature/FilterBar';
import MobileFilter from '@/components/feature/MobileFilter';
import Pager from '@/components/feature/Pager';
import { CATEGORY_GENRES, mediaList, type MediaType } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';
import { applyFilters } from '@/pages/category/page';

const PAGE_SIZE = 12;

const findTypeByGenre = (genre: string): MediaType | null => {
  const entry = Object.entries(CATEGORY_GENRES).find(([, genres]) => genres.includes(genre));
  return entry ? (entry[0] as MediaType) : null;
};

export default function ListPage() {
  const { category = '' } = useParams();
  const genreName = decodeURIComponent(category);
  const ownerType = findTypeByGenre(genreName);

  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters, genre: genreName });
  const [shown, setShown] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilters({ ...defaultFilters, genre: genreName });
    setShown(PAGE_SIZE);
  }, [genreName]);

  usePageMeta({
    title: `${genreName}影视列表 - 菠萝蜜网影视内容`,
    description: `${genreName}题材影视作品列表，按地区、年份与热度排序，快速筛选出你感兴趣的${genreName}电影与剧集。`,
    keywords: `${genreName},${genreName}影视,${genreName}推荐,影视列表`,
    path: `/list/${encodeURIComponent(genreName)}`,
  });

  const source = ownerType ? mediaList.filter((item) => item.type === ownerType) : mediaList;
  const results = useMemo(() => applyFilters(source, filters), [source, filters]);
  const genres = ownerType ? CATEGORY_GENRES[ownerType] : ['全部', genreName];

  const patchFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setShown(PAGE_SIZE);
  };

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb
          items={[
            ...(ownerType ? [{ label: ownerType, to: `/category/${encodeURIComponent(ownerType)}` }] : []),
            { label: `${genreName}列表` },
          ]}
        />

        <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-background-200 pb-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">
              {genreName}
              <span className="ml-2 text-sm font-normal text-foreground-500">影视列表</span>
            </h1>
            <p className="mt-1.5 text-xs text-foreground-500">
              共收录 {results.length} 部{genreName}作品{ownerType ? `，属于${ownerType}分类` : ''}。
            </p>
          </div>
          {ownerType && (
            <Link
              to={`/category/${encodeURIComponent(ownerType)}`}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition hover:border-primary-400 hover:text-primary-700"
            >
              返回{ownerType}分类
              <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-1">
          <div className="hidden lg:block">
            <FilterBar genres={genres} filters={filters} onChange={patchFilters} />
          </div>
          <div className="lg:hidden">
            <MobileFilter genres={genres} filters={filters} onChange={patchFilters} resultCount={results.length} />
          </div>
        </div>

        <div className="mt-3">
          <FilterSummary filters={filters} onReset={() => patchFilters(defaultFilters)} />
        </div>

        {results.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
            <h3 className="font-heading text-base font-bold text-foreground-950">该筛选条件下暂无作品</h3>
            <p className="mt-2 text-xs text-foreground-500">换一个地区或年份试试。</p>
            <button
              type="button"
              onClick={() => patchFilters(defaultFilters)}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
              {results.slice(0, shown).map((item, index) => (
                <MediaCard key={item.id} id={item.id} index={index} />
              ))}
            </div>
            <Pager total={results.length} shown={Math.min(shown, results.length)} pageSize={PAGE_SIZE} onChange={setShown} />
          </>
        )}
      </PortalShell>
    </SiteLayout>
  );
}